import React from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import MetaTags from './SEO/MetaTags';
import { generatePageSEO } from '../utils/SEOUtils';
import usePageContent from '../hooks/usePageContent';

/**
 * AboutPage - Displays about page content from API or fallback
 */
const AboutPage = () => {
  const seoData = generatePageSEO('about');
  const { page, loading, error } = usePageContent('about');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen text-white bg-gray-900">
        <MetaTags {...seoData} />
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          <div className="text-center mb-16">
            <div className="h-14 w-80 mx-auto bg-gray-700 rounded animate-pulse mb-6"></div>
            <div className="h-6 w-96 mx-auto bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-5/6"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-4/6"></div>
            <div className="h-32 bg-gray-800 rounded-lg animate-pulse mt-8"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // If we have API content, render it
  if (page && (page.html || page.body)) {
    const content = page.html || page.body;
    const title = page.page_name || page.nav_name || 'About ElectricNOW';

    return (
      <div className="min-h-screen text-white bg-gray-900">
        <MetaTags {...seoData} />
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
              {title}
            </h1>
          </div>

          {/* Dynamic Content */}
          <div
            className="prose prose-lg prose-invert max-w-none dynamic-page-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          {/* Contact CTA */}
          <section className="mt-16 text-center">
            <h2 className="text-3xl font-bold mb-6 text-[#9ec6f0]">Get in Touch</h2>
            <p className="mb-8 leading-relaxed text-gray-300">
              Have questions, suggestions, or just want to share your love for classic entertainment?
              We'd love to hear from you.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] text-white px-8 py-3 rounded-lg font-semibold hover:from-[#3d7fcb] hover:to-[#5a9de8] transition-all duration-300 transform hover:scale-105"
            >
              Contact Us
            </Link>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // Fallback to static content
  return (
    <div className="min-h-screen text-white bg-gray-900">
      <MetaTags {...seoData} />
      <Header />

      <main className="container max-w-4xl px-4 py-12 mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
            About ElectricNOW
          </h1>
          <p className="text-xl leading-relaxed text-gray-300">
            Where classic entertainment meets cutting-edge technology
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-[#9ec6f0]">Our Mission</h2>
          <div className="prose prose-lg prose-invert max-w-none">
            <p className="mb-6 leading-relaxed text-gray-300">
              Welcome to ElectricNOW, founded by entertainment professionals who are passionate
              lovers of movies and TV shows. Our mission is to bring you a carefully curated
              selection of classic movies, timeless TV shows, and so much more – all completely free.
            </p>
            <p className="leading-relaxed text-gray-300">
              ElectricNOW Network is a free streaming platform that aims to transport you back in
              time to the golden era of cinema and television. With our vast library of classic
              content, we offer a unique opportunity to relive the magic of iconic films and beloved
              series that shaped entertainment history.
            </p>
          </div>
        </section>

        {/* What Makes Us Special */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#9ec6f0]">What Makes ElectricNOW Special</h2>
          <div className="grid gap-8 md:grid-cols-2">
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-[#9ec6f0]">🎬 Completely Free</h3>
              <p className="text-gray-300">
                Enjoy our entire library without subscription fees or hidden costs. Quality
                entertainment should be accessible to everyone.
              </p>
            </div>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-[#9ec6f0]">✨ Curated Selection</h3>
              <p className="text-gray-300">
                Every title is handpicked by entertainment professionals to ensure you get the best
                of classic cinema and television.
              </p>
            </div>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-[#9ec6f0]">📺 High-Quality Streaming</h3>
              <p className="text-gray-300">
                Experience your favorite classics in crisp, high-quality streams that do justice to
                these timeless productions.
              </p>
            </div>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-[#9ec6f0]">🌐 Watch Anywhere</h3>
              <p className="text-gray-300">
                Compatible with all your devices – laptop, tablet, smartphone, or smart TV. Your
                entertainment travels with you.
              </p>
            </div>
          </div>
        </section>

        {/* AI Technology Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#9ec6f0]">Built with AI Innovation</h2>
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-lg p-8 border border-[#2f6eb0]/30">
            <div className="flex items-start mb-6 space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🤖</span>
              </div>
              <div>
                <h3 className="mb-4 text-2xl font-semibold text-white">Powered by Manus AI</h3>
                <p className="mb-4 leading-relaxed text-gray-300">
                  ElectricNOW represents a groundbreaking collaboration between human creativity and
                  artificial intelligence. Our platform was developed with the assistance of Manus
                  AI, an advanced AI agent that helped architect and build many of the sophisticated
                  features you experience today.
                </p>
              </div>
            </div>

            <div className="grid gap-6 mt-8 md:grid-cols-2">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-[#b3cfed]">🎯 Smart User Experience</h4>
                <p className="text-sm text-gray-300">
                  AI-powered interface design ensures intuitive navigation, responsive layouts, and
                  seamless interactions across all devices.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-[#b3cfed]">⚡ Intelligent Performance</h4>
                <p className="text-sm text-gray-300">
                  Advanced optimization algorithms ensure lightning-fast loading times and smooth
                  streaming experiences.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-[#b3cfed]">🔍 Content Discovery</h4>
                <p className="text-sm text-gray-300">
                  AI-enhanced categorization and recommendation systems help you discover hidden gems
                  from our classic collection.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-[#b3cfed]">🛡️ Quality Assurance</h4>
                <p className="text-sm text-gray-300">
                  Automated testing and quality control systems ensure consistent, reliable
                  performance across the platform.
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gray-900/50 rounded-lg border border-[#2f6eb0]/20">
              <p className="italic text-center text-gray-300">
                "The future of entertainment platforms lies in the seamless integration of human
                creativity and AI innovation. ElectricNOW is a testament to what's possible when
                technology serves storytelling."
              </p>
              <p className="text-[#9ec6f0] text-center mt-2 font-semibold">
                — The ElectricNOW Development Team
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-[#9ec6f0]">Our Story</h2>
          <div className="prose prose-lg prose-invert max-w-none">
            <p className="mb-6 leading-relaxed text-gray-300">
              ElectricNOW was born from a simple belief: everyone deserves access to quality
              entertainment. Our founders, seasoned entertainment industry professionals, recognized
              that while streaming services were proliferating, there was a gap in the market for
              classic, timeless content that didn't require expensive subscriptions.
            </p>
            <p className="mb-6 leading-relaxed text-gray-300">
              We set out to create more than just another streaming platform. We wanted to build a
              digital time machine that could transport viewers back to the golden age of cinema and
              television, when storytelling was king and every production was crafted with care and
              attention to detail.
            </p>
            <p className="leading-relaxed text-gray-300">
              Today, ElectricNOW stands as a bridge between the past and the future – preserving
              classic entertainment while leveraging cutting-edge technology to deliver it in new and
              exciting ways. We're not just streaming content; we're preserving cultural heritage and
              making it accessible to new generations of viewers.
            </p>
          </div>
        </section>

        {/* Technology & Innovation */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-[#9ec6f0]">Technology & Innovation</h2>
          <div className="p-8 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="mb-6 leading-relaxed text-gray-300">
              Behind ElectricNOW's sleek interface lies sophisticated technology designed to deliver
              the best possible viewing experience. Our platform utilizes advanced streaming
              protocols, adaptive bitrate technology, and intelligent content delivery networks to
              ensure smooth playback regardless of your internet connection.
            </p>
            <div className="grid gap-6 mt-8 md:grid-cols-3">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h4 className="mb-2 font-semibold">Lightning Fast</h4>
                <p className="text-sm text-gray-400">
                  Optimized for instant loading and seamless playback
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <h4 className="mb-2 font-semibold">Secure & Private</h4>
                <p className="text-sm text-gray-400">Your data and viewing habits are protected</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📱</span>
                </div>
                <h4 className="mb-2 font-semibold">Cross-Platform</h4>
                <p className="text-sm text-gray-400">Works seamlessly across all your devices</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-6 text-[#9ec6f0]">Get in Touch</h2>
          <p className="mb-8 leading-relaxed text-gray-300">
            Have questions, suggestions, or just want to share your love for classic entertainment?
            We'd love to hear from you.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] text-white px-8 py-3 rounded-lg font-semibold hover:from-[#3d7fcb] hover:to-[#5a9de8] transition-all duration-300 transform hover:scale-105"
          >
            Contact Us
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
